import { z } from 'zod'

import {
  MarketingContactStatus,
  MarketingSubjectType,
  MarketingUnsubscribeChannel,
} from '../constants/marketing'
import { dbEntitySchema, listQueryZodFields } from './system'
import { marketingEntityScopeSchema } from './marketingScope'
import { NotificationSettingType } from '../constants/notificationSetting'

export const marketingContactPhoneE164Regex = /^\+[1-9]\d{10,14}$/

export const marketingContactPhoneSchema = z
  .string()
  .trim()
  .regex(marketingContactPhoneE164Regex)
  .nullish()

export const marketingContactSchema = dbEntitySchema
  .and(marketingEntityScopeSchema)
  .and(
    z.object({
      subjectType: z.nativeEnum(MarketingSubjectType),
      subjectId: z.string().nullish(),
      email: z.string().email().nullish(),
      phone: marketingContactPhoneSchema,
      name: z.string().nullish(),
      status: z.nativeEnum(MarketingContactStatus),
      emailOptIn: z.boolean(),
      smsOptIn: z.boolean(),
      unsubscribedAt: z.date().nullish(),
      bouncedAt: z.date().nullish(),
      complainedAt: z.date().nullish(),
      providerSubscriberId: z.string().nullish(),
      providerInstanceId: z.string().nullish(),
    })
  )

export const marketingContactUnsubscribeArgsSchema = z.object({
  token: z.string().min(1),
  channel: z
    .nativeEnum(MarketingUnsubscribeChannel)
    .default(MarketingUnsubscribeChannel.EMAIL),
})

export const managementUserNotificationPreferenceSettingSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(NotificationSettingType),
  notifications: z.object({
    email: z.boolean(),
    sms: z.boolean(),
    push: z.boolean(),
  }),
  venueId: z.string().nullable(),
  venueName: z.string().nullable(),
})

export const managementUserNotificationPreferencesSchema = z.object({
  managementUserId: z.string(),
  email: z.string().nullable(),
  marketingEmailOptIn: z.boolean(),
  alreadyUnsubscribed: z.boolean(),
  notificationSettings: z.array(
    managementUserNotificationPreferenceSettingSchema
  ),
})

export const managementUserNotificationPreferencesTokenPreviewArgsSchema =
  z.object({
    token: z.string().min(1),
  })

export const managementUserNotificationPreferencesTokenUpdateArgsSchema =
  z.object({
    token: z.string().min(1),
    marketingEmailOptIn: z.boolean(),
    notificationSettings: z
      .array(
        z.object({
          id: z.string().min(1),
          email: z.boolean(),
        })
      )
      .optional(),
  })

export const managementUserNotificationPreferencesUpdateArgsSchema = z.object({
  marketingEmailOptIn: z.boolean(),
})

const nullableBooleanQuerySchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.coerce.boolean().optional()
)

export const marketingContactsListQuerySchema = z.object({
  ...listQueryZodFields,
  search: z.string().optional().nullable(),
  emailOptIn: nullableBooleanQuerySchema,
  smsOptIn: nullableBooleanQuerySchema,
})

export const marketingContactsListResponseSchema = z.object({
  data: z.array(marketingContactSchema),
})

export const marketingContactAdminUpdateByIdArgsSchema = z
  .object({
    emailOptIn: z.boolean().optional(),
    smsOptIn: z.boolean().optional(),
  })
  .refine(
    (data) => data.emailOptIn !== undefined || data.smsOptIn !== undefined,
    {
      message: 'emailOptIn or smsOptIn required',
    }
  )
